import React, { useEffect, useMemo, useState } from "react";
import { MoreVert as MoreVertIcon } from "@mui/icons-material";
import { Link } from "react-router-dom";
import api from "../../../../config/URL";
import TaxAdd from "./TaxAdd";
import TaxEdit from "./TaxEdit";
import {
  createTheme,
  IconButton,
  Menu,
  MenuItem,
  ThemeProvider,
} from "@mui/material";
import {
  MaterialReactTable,
  useMaterialReactTable,
} from "material-react-table";
import GlobalDelete from "../../../../components/common/GlobalDelete";
import { exportVisibleColumnsToCSV } from "../../../../components/common/ExportCsv";

const Tax = () => {
  const [datas, setDatas] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedId, setSelectedId] = useState(null);
  const [menuAnchor, setMenuAnchor] = useState(null);
  /* -------------------- ROLE MATRIX -------------------- */
  const storedScreens = JSON.parse(localStorage.getItem("screens") || "{}");

  const canIndex = Boolean(storedScreens?.taxSettingIndex);
  const canRead = Boolean(storedScreens?.taxSettingRead);
  const canCreate = Boolean(storedScreens?.taxSettingCreate);
  const canUpdate = Boolean(storedScreens?.taxSettingUpdate);
  const canDelete = Boolean(storedScreens?.taxSettingDelete);

  const showActionColumn = canUpdate || canDelete;

  const getData = async () => {
    try {
      const response = await api.get("/getAllTaxSetting");
      setDatas(response.data);
    } catch (error) {
      console.error("Error fetching data:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    getData();
  }, []);

  /* -------------------- COLUMNS -------------------- */
  const columns = useMemo(() => {
    const baseColumns = [
      {
        accessorFn: (row, index) => index + 1,
        header: "S.NO",
        size: 40,
        Cell: ({ cell }) => (
          <span style={{ textAlign: "center" }}>{cell.getValue()}</span>
        ),
      },
      {
        accessorKey: "status",
        header: "Status",
        size: 40,
        Cell: ({ row }) =>
          row.original.status === "ACTIVE" ? (
            <span className="badge badges-Green fw-light">Active</span>
          ) : row.original.status === "INACTIVE" ? (
            <span className="badge badges-orange fw-light">Inactive</span>
          ) : null,
      },
      {
        accessorKey: "taxType",
        header: "Tax Type",
      },
      {
        accessorKey: "rate",
        header: "Rate",
      },
      {
        accessorKey: "effectiveDate",
        header: "Effective Date",
      },
      { accessorKey: "createdBy", header: "Created By" },
      {
        accessorKey: "createdAt",
        header: "Created On",
        Cell: ({ cell }) => cell.getValue()?.substring(0, 10),
      },
      {
        accessorKey: "updatedAt",
        header: "Updated On",
        Cell: ({ cell }) => cell.getValue()?.substring(0, 10) || "",
      },
      {
        accessorKey: "updatedBy",
        header: "Updated By",
        Cell: ({ cell }) => cell.getValue() || "",
      },
    ];

    if (!showActionColumn) return baseColumns;

    return [
      baseColumns[0], // S.NO
      {
        accessorKey: "id",
        header: "",
        size: 20,
        enableSorting: false,
        Cell: ({ cell }) => (
          <IconButton
            onClick={(e) => {
              e.stopPropagation();
              setMenuAnchor(e.currentTarget);
              setSelectedId(cell.getValue());
            }}
          >
            <MoreVertIcon />
          </IconButton>
        ),
      },
      ...baseColumns.slice(1),
    ];
  }, [showActionColumn]);

  /* -------------------- TABLE INSTANCE -------------------- */
  const table = useMaterialReactTable({
    columns,
    data: datas,
    enableColumnActions: false,
    enableColumnFilters: false,
    enableDensityToggle: false,
    enableFullScreenToggle: false,
    initialState: {
      pagination: { pageSize: 50, pageIndex: 0 },
      columnVisibility: {
        createdBy: false,
        createdAt: false,
        updatedBy: false,
        updatedAt: false,
      },
    },
    renderTopToolbarCustomActions: ({ table }) => (
      <button
        className="btn btn-sm btn-border"
        onClick={() => exportVisibleColumnsToCSV(table, datas, "Tax.csv")}
      >
        Export CSV
      </button>
    ),
  });

  /* -------------------- THEME -------------------- */
  const theme = createTheme({
    components: {
      MuiTableCell: {
        styleOverrides: {
          head: {
            color: "#535454 !important",
            backgroundColor: "#e6edf7 !important",
            fontWeight: "400 !important",
            fontSize: "13px !important",
            textAlign: "center !important",
          },
        },
      },
      MuiSwitch: {
        styleOverrides: {
          root: {
            "&.Mui-disabled .MuiSwitch-track": {
              backgroundColor: "#f5e1d0",
              opacity: 1,
            },
            "&.Mui-disabled .MuiSwitch-thumb": {
              color: "#eb862a",
            },
          },
          track: { backgroundColor: "#e0e0e0" },
          thumb: { color: "#eb862a" },
          switchBase: {
            "&.Mui-checked": { color: "#eb862a" },
            "&.Mui-checked + .MuiSwitch-track": {
              backgroundColor: "#eb862a",
            },
          },
        },
      },
    },
  });

  const handleMenuClose = () => setMenuAnchor(null);

  return (
    <div className="container-fluid center">
      <div className="d-flex justify-content-between align-items-center mb-2">
        <ol className="breadcrumb" style={{ listStyle: "none", margin: 0 }}>
          <li>
            <Link to="/" className="custom-breadcrumb">
              Home
            </Link>
            <span className="breadcrumb-separator"> &gt; </span>
          </li>
          <li>
            &nbsp;Settings<span className="breadcrumb-separator"> &gt; </span>
          </li>
          <li>
            &nbsp;Master Settings
            <span className="breadcrumb-separator"> &gt; </span>
          </li>
          <li className="breadcrumb-item active">&nbsp;Tax</li>
        </ol>

        {canCreate && <TaxAdd onSuccess={getData} />}
      </div>

      <div className="card">
        <div
          className="d-flex justify-content-between align-items-center p-1"
          style={{ background: "#f5f7f9" }}
        >
          <div className="d-flex align-items-center">
            <div className="dot active"></div>
            <span className="ms-2 text-muted">
              This database shows the list of{" "}
              <span className="bold" style={{ color: "#287f71" }}>
                Tax
              </span>
            </span>
          </div>
        </div>

        {loading ? (
          <div className="loader-container">
            <div className="loading">
              <span></span>
              <span></span>
              <span></span>
              <span></span>
              <span></span>
            </div>
          </div>
        ) : (
          <>
            <ThemeProvider theme={theme}>
              <MaterialReactTable table={table} />
            </ThemeProvider>

            {showActionColumn && (
              <Menu
                anchorEl={menuAnchor}
                open={Boolean(menuAnchor)}
                onClose={handleMenuClose}
              >
                {canUpdate && (
                  <MenuItem>
                    <TaxEdit
                      id={selectedId}
                      onSuccess={getData}
                      handleMenuClose={handleMenuClose}
                    />
                  </MenuItem>
                )}
                {canDelete && (
                  <MenuItem>
                    <GlobalDelete
                      path={`/deleteTaxSetting/${selectedId}`}
                      onDeleteSuccess={getData}
                      onOpen={handleMenuClose}
                    />
                  </MenuItem>
                )}
              </Menu>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default Tax;
